import PropTypes from 'prop-types';
import React from 'react';
import { isMobileOnly } from 'react-device-detect';
import { Row } from 'reactstrap';
import styled from 'styled-components';
import { ColNoPadding } from '../../../../../components/Common';
import ExpandIcon from '../../../../../components/ExpandIcon';
import InfoText from '../../../../../components/InfoText';
import ListItem from '../../../../../components/ListItem';
import { A, B, CAC_TYPE, OP } from '../../../../../res/Constants';
import { GreenColumn, GreenTimeLine, RedColumn, RedTimeLine, TitleRow } from './Common';

GreenRedTimeLine.propTypes = {
    cacType: PropTypes.number.isRequired,
    title: PropTypes.string,
    lines: PropTypes.shape({
        lineA: PropTypes.shape({
            title: PropTypes.string,
            dates: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string,
                    id: PropTypes.string,
                    date: PropTypes.number,
                    dates: PropTypes.arrayOf(PropTypes.number),
                    isMultiDate: PropTypes.bool
                })
            ),
            extraContent: PropTypes.shape({
                name: PropTypes.string,
                content: PropTypes.string
            })
        }).isRequired,
        lineB: PropTypes.shape({
            title: PropTypes.string,
            dates: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string,
                    id: PropTypes.string,
                    date: PropTypes.number,
                    dates: PropTypes.arrayOf(PropTypes.number),
                    isMultiDate: PropTypes.bool
                })
            ),
            extraContent: PropTypes.shape({
                name: PropTypes.string,
                content: PropTypes.string
            })
        }).isRequired,
        canBeEditable: PropTypes.bool
    }),
    lineCount: PropTypes.number,
    lineLabels: PropTypes.arrayOf(PropTypes.string),
    onSave: PropTypes.func,
    onDateEdited: PropTypes.func,
    onAddDate: PropTypes.func,
    onRemoveDate: PropTypes.func,
    onDeleteDate: PropTypes.func,
    onCancel: PropTypes.func
};

function GreenRedTimeLine({
    cacType,
    title,
    lines,
    lineCount,
    lineLabels,
    canBeEditable = false,
    onDateEdited,
    onAddDate,
    onRemoveDate,
    onDeleteDate,
    onCancel,
    onSave
}) {
    const onDateEditedParser = (line, id, dateOrIndex, newDate) => {
        if (onDateEdited) onDateEdited(line, id, dateOrIndex, newDate);
    };

    const onAddDateParser = (line, id) => {
        if (onAddDate) onAddDate(line, id);
    };

    const onRemoveDateParser = (line, id, index) => {
        if (onRemoveDate) onRemoveDate(line, id, index);
    };

    const onDeleteDateParser = (line, id) => {
        if (onDeleteDate) onDeleteDate(line, id);
    };

    const onCancelParser = (line) => {
        if (onCancel) onCancel(line);
    };

    // Default to legacy behavior if lineLabels not provided
    const labels = lineLabels

    return lines == null ? null : isMobileOnly ? (
        <MobileLayout
            cacType={cacType}
            title={title}
            lines={lines}
            lineCount={lineCount}
            lineLabels={labels}
            canBeEditable={canBeEditable}
            onDateEdited={onDateEditedParser}
            onAddDate={onAddDateParser}
            onRemoveDate={onRemoveDateParser}
            onDeleteDate={onDeleteDateParser}
            onCancel={onCancelParser}
            onSave={onSave}
        />
    ) : (
        <DesktopLayout
            cacType={cacType}
            lines={lines}
            lineCount={lineCount}
            lineLabels={labels}
            canBeEditable={canBeEditable}
            onDateEdited={onDateEditedParser}
            onAddDate={onAddDateParser}
            onRemoveDate={onRemoveDateParser}
            onDeleteDate={onDeleteDateParser}
            onCancel={onCancelParser}
            onSave={onSave}
        />
    );
}

const DesktopLayout = ({ cacType, lines, lineCount, lineLabels, canBeEditable, onDateEdited, onAddDate, onRemoveDate, onDeleteDate, onCancel, onSave }) => {
    return (
        <Row>
            <GreenColumn md='5'>
                <ListItem>
                    {() => (
                        <>
                            <h5>{`Line ${lineLabels[0]}`}</h5>
                            <GreenTimeLine
                                title={lines.lineA.title}
                                dates={lines.lineA.dates}
                                canBeEditable={canBeEditable}
                                onSave={() => onSave && onSave(A)}
                                onDateEdited={(id, dateOrIndex, newDate) => onDateEdited(A, id, dateOrIndex, newDate)}
                                onAddDate={(id) => onAddDate(A, id)}
                                onRemoveDate={(id, index) => onRemoveDate(A, id, index)}
                                onDeleteDate={(id) => onDeleteDate(A, id)}
                                onCancel={() => onCancel(A)}
                            />
                            {lines.lineA.extraContent && (
                                <StyledInfotext
                                    title={lines.lineA.extraContent.name}
                                    content={lines.lineA.extraContent.content}
                                />
                            )}
                        </>
                    )}
                </ListItem>
            </GreenColumn>
            {cacType === CAC_TYPE.CROP && lineCount > 1 && (
                <RedColumn
                    md={{
                        size: 5,
                        offset: 1
                    }}>
                    <ListItem>
                        {() => (
                            <>
                                <h5>{`Line ${lineLabels[1]}`}</h5>
                                <RedTimeLine
                                    title={lines.lineB.title}
                                    dates={lines.lineB.dates}
                                    canBeEditable={canBeEditable}
                                    onSave={() => onSave && onSave(B)}
                                    onDateEdited={(id, dateOrIndex, newDate) => onDateEdited(B, id, dateOrIndex, newDate)}
                                    onAddDate={(id) => onAddDate(B, id)}
                                    onRemoveDate={(id, index) => onRemoveDate(B, id, index)}
                                    onDeleteDate={(id) => onDeleteDate(B, id)}
                                    onCancel={() => onCancel(B)}
                                />
                                {lines.lineB.extraContent && (
                                    <StyledInfotext
                                        title={lines.lineB.extraContent.name}
                                        content={lines.lineB.extraContent.content}
                                    />
                                )}
                            </>
                        )}
                    </ListItem>
                </RedColumn>
            )}
        </Row>
    );
};

const MobileLayout = ({
    cacType,
    title,
    lines,
    lineCount,
    lineLabels,
    canBeEditable,
    onDateEdited,
    onAddDate,
    onRemoveDate,
    onDeleteDate,
    onCancel,
    onSave
}) => {
    const expandableChildren = (
        <Row>
            <GreenColumn>
                <h5>{`Line ${lineLabels[0]}`}</h5>
                <GreenTimeLine
                    title={lines.lineA.title}
                    dates={lines.lineA.dates}
                    canBeEditable={canBeEditable}
                    onSave={() => onSave && onSave(A)}
                    onDateEdited={(id, dateOrIndex, newDate) => onDateEdited(A, id, dateOrIndex, newDate)}
                    onAddDate={(id) => onAddDate(A, id)}
                    onRemoveDate={(id, index) => onRemoveDate(A, id, index)}
                    onDeleteDate={(id) => onDeleteDate(A, id)}
                    onCancel={() => onCancel(A)}
                />
                {lines.lineA.extraContent && (
                    <StyledInfotext
                        title={lines.lineA.extraContent.name}
                        content={lines.lineA.extraContent.content}
                    />
                )}
            </GreenColumn>
            {cacType === CAC_TYPE.CROP && lineCount > 1 && (
                <RedColumn>
                    <h5>{`Line ${lineLabels[1]}`}</h5>
                    <RedTimeLine
                        title={lines.lineB.title}
                        dates={lines.lineB.dates}
                        canBeEditable={canBeEditable}
                        onSave={() => onSave && onSave(B)}
                        onDateEdited={(id, dateOrIndex, newDate) => onDateEdited(B, id, dateOrIndex, newDate)}
                        onAddDate={(id) => onAddDate(B, id)}
                        onRemoveDate={(id, index) => onRemoveDate(B, id, index)}
                        onDeleteDate={(id) => onDeleteDate(B, id)}
                        onCancel={() => onCancel(B)}
                    />
                    {lines.lineB.extraContent && (
                        <StyledInfotext
                            title={lines.lineB.extraContent.name}
                            content={lines.lineB.extraContent.content}
                        />
                    )}
                </RedColumn>
            )}
        </Row>
    );

    return (
        <StyledListItem expandableChildren={expandableChildren}>
            {(renderProps) => (
                <>
                    <TitleRow>
                        <ColNoPadding xs='8' md='12'>
                            <h5>{title}</h5>
                        </ColNoPadding>
                        <ColNoPadding className='d-md-none' xs='auto'>
                            <ExpandIcon onClick={renderProps.toggleExpand} />
                        </ColNoPadding>
                    </TitleRow>
                </>
            )}
        </StyledListItem>
    );
};


const StyledListItem = styled(ListItem)`
    .inner-container-expandable {
        padding: 0;
    }
`;

const StyledInfotext = styled(InfoText)`
    padding-bottom: 15px;
`;

export default GreenRedTimeLine;
